/**
 * Survival-based Confidence Interval Adaptation
 * Shared utilities for narrowing confidence intervals based on survival time.
 */

export interface SurvivalAdaptationConfig {
  useSurvivalAdaptation: boolean;
  survivalAdaptationStrength: number;  // 0.1-0.5
  earlyStageThreshold: number;         // 0.3-0.5
}

export interface SurvivalAdjustmentResult {
  survivalFraction: number;
  adjustmentFactor: number;
  isEarlyStage: boolean;
  isLateStage: boolean;
}

/**
 * Calculate survival-based adjustment for confidence intervals
 *
 * @param currentTime Current degradation/survival time
 * @param referenceTime Reference time (typically median failure time)
 * @param config Survival adaptation configuration
 * @returns Adjustment factor and metadata
 */
export function calculateSurvivalAdjustment(
  currentTime: number,
  referenceTime: number,
  config: SurvivalAdaptationConfig
): SurvivalAdjustmentResult {

  // Calculate survival fraction (0 = just started, 1 = at median, >1 = beyond median)
  const survivalFraction = currentTime / referenceTime;

  // Determine stage
  const isEarlyStage = survivalFraction < config.earlyStageThreshold;
  const isLateStage = survivalFraction > 0.7;

  if (!config.useSurvivalAdaptation || isEarlyStage) {
    // No adjustment in early stage or if disabled
    return {
      survivalFraction,
      adjustmentFactor: 1.0,
      isEarlyStage,
      isLateStage
    };
  }

  // Calculate progressive adjustment
  // Maps survival fraction to adjustment factor
  // threshold -> 1.0 (no adjustment)
  // 1.0+ -> continues narrowing for long survivors (no cap)

  const normalizedProgress = (survivalFraction - config.earlyStageThreshold) / (1.0 - config.earlyStageThreshold);

  // Apply non-linear transformation for smoother adjustment
  // Use cube root for even more gradual narrowing
  const smoothedProgress = Math.cbrt(normalizedProgress);

  // Calculate adjustment factor with improved floor
  // Apply gentler narrowing especially for late-stage predictions
  const gentleStrength = config.survivalAdaptationStrength * 0.6; // Reduce strength by 40%
  const adjustmentFactor = 1.0 - (smoothedProgress * gentleStrength);

  // Ensure reasonable minimum (never narrow more than 50%)
  const finalAdjustment = Math.max(0.5, adjustmentFactor);

  return {
    survivalFraction,
    adjustmentFactor: finalAdjustment,
    isEarlyStage,
    isLateStage
  };
}

/**
 * Apply survival adjustment to confidence interval percentiles
 * Narrows the percentiles towards the median based on survival
 *
 * @param lowerPercentile Original lower percentile (e.g., 0.15)
 * @param upperPercentile Original upper percentile (e.g., 0.85)
 * @param adjustmentFactor Survival adjustment factor from calculateSurvivalAdjustment
 * @returns Adjusted percentiles
 */
export function adjustPercentilesForSurvival(
  lowerPercentile: number,
  upperPercentile: number,
  adjustmentFactor: number
): { adjustedLower: number; adjustedUpper: number } {

  if (adjustmentFactor === 1.0) {
    // No adjustment needed
    return {
      adjustedLower: lowerPercentile,
      adjustedUpper: upperPercentile
    };
  }

  // Calculate distance from median (0.5)
  const lowerDistance = 0.5 - lowerPercentile;
  const upperDistance = upperPercentile - 0.5;

  // Apply adjustment symmetrically
  const adjustedLower = 0.5 - (lowerDistance * adjustmentFactor);
  const adjustedUpper = 0.5 + (upperDistance * adjustmentFactor);

  return {
    adjustedLower: Math.max(0.05, adjustedLower),  // Never go below 5th percentile
    adjustedUpper: Math.min(0.95, adjustedUpper)   // Never go above 95th percentile
  };
}

/**
 * Apply survival adjustment to absolute confidence bounds
 * Used when working with actual VBL values rather than percentiles
 *
 * @param median Median VBL value
 * @param lower Lower confidence bound
 * @param upper Upper confidence bound
 * @param adjustmentFactor Survival adjustment factor
 * @returns Adjusted confidence bounds
 */
export function adjustBoundsForSurvival(
  median: number,
  lower: number,
  upper: number,
  adjustmentFactor: number
): { adjustedLower: number; adjustedUpper: number } {

  if (adjustmentFactor === 1.0) {
    return {
      adjustedLower: lower,
      adjustedUpper: upper
    };
  }

  // Calculate width from median
  const lowerWidth = median - lower;
  const upperWidth = upper - median;

  // Apply adjustment
  const adjustedLower = Math.max(1, median - (lowerWidth * adjustmentFactor));
  const adjustedUpper = median + (upperWidth * adjustmentFactor);

  return {
    adjustedLower,
    adjustedUpper
  };
}

/**
 * Format survival adjustment for logging/display
 */
export function formatSurvivalAdjustment(result: SurvivalAdjustmentResult): string {
  const percentage = (result.survivalFraction * 100).toFixed(1);
  const reduction = ((1 - result.adjustmentFactor) * 100).toFixed(1);

  if (result.isEarlyStage) {
    return `Early stage (${percentage}% of median) - No CI adjustment`;
  } else if (result.isLateStage) {
    return `Late stage (${percentage}% of median) - CI width reduced by ${reduction}%`;
  } else {
    return `Mid stage (${percentage}% of median) - CI width reduced by ${reduction}%`;
  }
}